import { createMemo, createResource, createSignal, onCleanup, onMount, type Resource } from 'solid-js';
import Cart from '@brytdesigns/shopify-ajax-cart';

export type RecommendedProduct = {
  id: number;
  title: string;
  url: string;
  price: number;
  price_min: number;
  price_max: number;
  price_varies: boolean;
  compare_at_price: number | null;
  featured_image: string;
  variants: { id: number }[];
  available: boolean;
  tags: string[];
};

const recsCache = new Map<number, RecommendedProduct[]>();

export const fetchRecommendationsForProduct = async (
  baseUrl: string,
  productId: number,
  limit: number,
  intent: string,
): Promise<RecommendedProduct[]> => {
  if (recsCache.has(productId)) return recsCache.get(productId)!;

  const url = `${baseUrl}.json?product_id=${productId}&limit=${limit}&intent=${intent}`;
  const response = await fetch(url);
  if (!response.ok) return [];

  try {
    const data = await response.json();
    const products: RecommendedProduct[] = data.products || [];
    recsCache.set(productId, products);
    return products;
  } catch {
    return [];
  }
};

export const formatMoney = (cents: number): string => {
  if (window.Shopify?.formatMoney) {
    return window.Shopify.formatMoney(cents, window.Shopify.money_format);
  }
  return `$${(cents / 100).toFixed(2)}`;
};

export type UseCartRecommendationsOptions = {
  element: HTMLElement;
  baseUrl: string;
  cartUrl: string;
  limit: number;
  intent: string;
};

export type UseCartRecommendationsResult = {
  recommendations: () => RecommendedProduct[];
  isLoading: () => boolean;
  hasItems: () => boolean;
  resource: Resource<RecommendedProduct[] | undefined>;
};

export const useCartRecommendations = (opts: UseCartRecommendationsOptions): UseCartRecommendationsResult => {
  const [isInView, setIsInView] = createSignal(false);

  const observer = new IntersectionObserver(
    (entries) => {
      if (entries[0]?.isIntersecting) {
        setIsInView(true);
        observer.disconnect();
      }
    },
    { rootMargin: '200px' },
  );

  onMount(() => observer.observe(opts.element));
  onCleanup(() => observer.disconnect());

  const cartItemProductIds = createMemo(() => {
    if (!isInView()) return null;
    const items = Cart.data?.items || [];
    return items.map((item) => Number(item.product_id));
  });

  const [resource] = createResource(cartItemProductIds, async (productIds) => {
    if (!productIds || productIds.length === 0) return [];

    const seenIds = new Set<number>();
    const combined: RecommendedProduct[] = [];

    for (const id of productIds) {
      const recs = await fetchRecommendationsForProduct(opts.baseUrl, id, opts.limit, opts.intent);
      for (const rec of recs) {
        if (!seenIds.has(rec.id)) {
          seenIds.add(rec.id);
          combined.push(rec);
        }
      }
      if (combined.length >= opts.limit) break;
    }

    return combined;
  });

  const recommendations = createMemo(() => {
    const raw = resource() || [];
    const cartIds = new Set(cartItemProductIds() || []);
    return raw.filter((rec) => !cartIds.has(rec.id)).slice(0, opts.limit);
  });

  const isLoading = () => resource.loading;
  const hasItems = () => isInView() && (isLoading() || recommendations().length > 0);

  return { recommendations, isLoading, hasItems, resource };
};
